import React from 'react';
import axios from 'axios';
import type { FormikValues } from 'formik';
import { Formik } from 'formik';
import { get } from 'lodash';

import mixpanel from '~/components/CmsMain/mixpanel';
import { FsIndexPanel, LoadingSwitch } from '~/components/core';
import SectionWrapper from '~/components/Fnol/NewFnolUI/SectionWrapper';
import FnolPrompt from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/FnolPrompt/FnolPrompt';
import Header from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/Header/Header';
import fnolConfigurationSchema from '~/components/SystemConfiguration/FnolConfiguration/fnolConfigurationValidationSchema';
import ConfigurationBreadcrumbs from '~/components/SystemConfiguration/IncidentConfigurationPage/ConfigurationBreadcrumbs';
import GlobalFieldsDriftBanner from '~/components/SystemConfiguration/IncidentConfigurationPage/GlobalFieldsDriftBanner';
import StaleDraftsConfirmModal from '~/components/SystemConfiguration/IncidentConfigurationPage/StaleDraftsConfirmModal';
import { useStaleDraftsSaveGuard } from '~/components/SystemConfiguration/IncidentConfigurationPage/useStaleDraftsSaveGuard';
import { setContactConfiguredFieldsKeysInValues } from '~/components/SystemConfiguration/IncidentConfigurationPage/utils';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import { FNOL_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import type { IncidentConfigurationModelGlobalFieldsStatus } from '~/server_shared/openapi/data-contracts';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import type { ConfigurationScreenProps } from '../../IncidentConfigurationPage/types';

import CoveredVehicleSection from './Sections/CoveredVehicleSection';
import GeneralCoveredObjectSection from './Sections/GeneralCoveredObjectSection';
import PolicyDetailsSection from './Sections/PolicyDetailsSection';
import copies from './copies.json';
import PolicyConfigurationFooter from './PolicyConfigurationFooter';

const getCoveredObjectSection = (fnolConfiguration: { lob: string }) => {
  if (fnolConfiguration?.lob === 'auto_claim') {
    return [
      {
        id: 'covered_vehicle',
        title: 'Involved Property',
        node: <CoveredVehicleSection />,
      },
    ];
  }

  const coveredObjects = get(fnolConfiguration, 'configuration.policy.covered_objects', []);
  if (!coveredObjects || coveredObjects.length === 0) return [];

  return coveredObjects.map((coveredObject: { desc: string; id: string }, index: number) => ({
    id: `covered_object_${index}`,
    title: coveredObject.desc,
    node: <GeneralCoveredObjectSection coveredObjectIndex={index} />,
  }));
};

interface LiveIncidentConfiguration {
  lob: unknown;
  configuration: unknown;
  // The per-environment edit counter a live save bumps; drafts based on an older value are dropped.
  version: number;
  global_fields_status?: IncidentConfigurationModelGlobalFieldsStatus | null;
}

const PolicyConfigurationScreen: React.FC<ConfigurationScreenProps> = ({
  incidentConfigurationId,
  subOrgId,
  onCancel,
  onSubmit,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();

  const isSaveDraftEnabled = isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.INCIDENT_CONFIGURATION_DRAFTS);

  const incidentConfigurationsRoute = `/sysconfig/api/v1/organizations/${organization.id}/incident_configurations/${incidentConfigurationId}`;
  const {
    isLoading,
    isError,
    // LoadingSwitch keeps the form (and therefore the save handler) unmounted until this resolves,
    // so the empty placeholder is never the object the handler reads.
    data: fnolConfiguration = {} as LiveIncidentConfiguration,
    reloadData: reloadConfiguration,
  } = useDataFetcher<LiveIncidentConfiguration>(`${incidentConfigurationsRoute}`, {});

  const trackMixpanelEvent = () => {
    mixpanel.track(FNOL_MIXPANEL_EVENTS.SAVE_POLICY_CONFIGURATION_CLICKED, {
      incident_configuration_id: incidentConfigurationId,
      sub_organization_id: subOrgId,
    });
  };

  const savePolicyConfiguration = async (values: FormikValues) => {
    trackMixpanelEvent();
    values = setContactConfiguredFieldsKeysInValues(values);

    await axios.patch(incidentConfigurationsRoute, {
      old_configuration: fnolConfiguration,
      new_configuration: values,
    });
    await onSubmit();
  };

  // A policy save PATCHes the same incident configuration as the FNOL editor, so it bumps the same
  // version and invalidates the same drafts.
  const { guardedSave, confirmModalProps } = useStaleDraftsSaveGuard({
    isEnabled: isSaveDraftEnabled,
    organizationId: organization.id,
    onSave: savePolicyConfiguration,
  });

  return (
    <LoadingSwitch isLoading={isLoading} isError={isError}>
      <Formik
        initialValues={{ ...fnolConfiguration, sub_organization_id: subOrgId }}
        enableReinitialize
        validationSchema={fnolConfigurationSchema(fnolConfiguration)}
        onSubmit={async (values, formikProps) => {
          const { setSubmitting } = formikProps;
          try {
            setSubmitting(true);
            await guardedSave(values, {
              setSubmitting,
              incidentConfigurationId,
              liveVersion: fnolConfiguration.version,
            });
          } catch (error) {
            setSubmitting(false);
          }
        }}
      >
        <>
          <FnolPrompt />
          <StaleDraftsConfirmModal {...confirmModalProps} />
          <div className="fixed z-10 w-full bg-slate-200 p-12">
            <ConfigurationBreadcrumbs
              onCancel={onCancel}
              externalAdminRoute="/policy_configuration"
              configurationName="Policy Configuration"
            />
          </div>
          {/* Gap between start of sections */}
          {Array.from(Array(2).keys()).map((item, index) => (
            <SectionWrapper key={index} />
          ))}
          <Header title={copies.header.title} subtitle={copies.header.subtitle} />
          <GlobalFieldsDriftBanner
            globalFieldsStatus={fnolConfiguration.global_fields_status}
            incidentConfigurationRoute={incidentConfigurationsRoute}
            reloadConfiguration={reloadConfiguration}
          />
          <div className="bg-slate-200 pt-32">
            <div>
              <FsIndexPanel
                sections={[
                  {
                    id: 'policy_details',
                    title: 'Policy Details',
                    node: <PolicyDetailsSection />,
                    isCollapsible: false,
                    children: [],
                  },
                  // @ts-expect-error fnolConfiguration is not typed
                  ...getCoveredObjectSection(fnolConfiguration),
                ]}
                panelTitle="Policy Content"
                SectionWrapperComponent={SectionWrapper}
                sectionContainerClassName="scroll-mt-40"
                handleScrollTopOffset={40}
              />
              {/* Gap between end of sections and the footer */}
              {Array.from(Array(30).keys()).map((item, index) => (
                <SectionWrapper key={index} />
              ))}
            </div>
          </div>
          <PolicyConfigurationFooter onCancel={onCancel} />
        </>
      </Formik>
    </LoadingSwitch>
  );
};

export default PolicyConfigurationScreen;
